import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { noop } from 'ui-components/default-props/noop';

import { emptyArray } from 'ui-components/default-props/empty';

import { useYupFormValidation } from '../../validation/use-yup-form-validation';
import shipFromSchema from '../schema';

import {
  addNewShipFrom as addNewShipFromAction,
  editShipFrom as editShipFromAction,
  updateSellerInventoryWarehouses,
} from '../../store/seller/seller.actions';
import { clearShippyPendingPrefill } from '../../store/shippy/shippy.actions';
import checkAddressEquality from '../services/address-equality-service';
import { addShipFrom, editShipFrom } from '../services/api';
import { saveShipFromLtlDefaults } from '@shipstation/client-common/api/ltl.api';
import { trackShipFromConfigComplete } from '../tracking-events';
import { useShipFroms } from '../../hooks/ship-froms/use-ship-froms';
import { getInventoryWarehouses } from '../../store/inventory/inventory.api';
import { NotificationService, NOTIFICATION_TYPE_WARNING } from 'ui-components/services/notification-service';
import { shipFromContextMessages } from './ship-from-context-messages';

const update = {
  request: editShipFrom,
  action: editShipFromAction,
};

const create = {
  request: addShipFrom,
  action: addNewShipFromAction,
};

const getRequestAndAction = (shipFromId) => {
  return shipFromId ? update : create;
};

const ShipFromContext = React.createContext(null);
export const useShipFromContext = () => useContext(ShipFromContext);

/**
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {{ originAddress: object, returnAddress: object }} props.shipFrom
 * @param {(result: object, warehouses?: object[]) => void} props.onSuccess
 * @param {(error: object) => void} props.onError
 * @param {() => void} [props.onBack]
 * @param {(shipFrom: object) => void} [props.onShipFromBack]
 * @param {boolean} [props.shouldHideTimezone]
 * @param {() => void} [props.trackInputChange]
 * @param {() => void} [props.trackInputClick]
 * @param {Array} props.sources
 * @param {boolean} [props.isInventorySourcesLoading]
 * @param {string} [props.source]
 * @param {boolean} [props.shouldCreateInventoryWarehouse]
 * @param {object | null} [props.prefillAddress]
 */
export const ShipFromContextProvider = ({
  children,
  shipFrom,
  onSuccess,
  onError,
  onBack = noop,
  onShipFromBack = noop,
  shouldHideTimezone = false,
  trackInputChange = noop,
  trackInputClick = noop,
  sources,
  isInventorySourcesLoading = false,
  source = '',
  shouldCreateInventoryWarehouse = false,
  prefillAddress = null,
}) => {
  const intl = useIntl();
  const [contextShipFrom, setContextShipFrom] = useState(shipFrom);
  const [returnAddressSameAsShipFrom, setReturnAddressSameAsShipFrom] = useState(
    checkAddressEquality(shipFrom.originAddress, shipFrom.returnAddress),
  );
  const dispatch = useDispatch();

  const shipFromRef = useRef(shipFrom);
  shipFromRef.current = shipFrom;

  useEffect(() => {
    if (!sources.length) return;
    setContextShipFrom((prev) => {
      if (prev.inventorySourceId) return prev;
      const { inventorySourceType, inventorySourceId, inventorySourceExternalId } = shipFromRef.current;
      if (!inventorySourceId) return prev;
      return { ...prev, inventorySourceType, inventorySourceId, inventorySourceExternalId };
    });
  }, [sources]);

  const [loading, setLoading] = useAsyncSafeState(false);
  const [errors, setErrors] = useAsyncSafeState(emptyArray);
  const [showValidation, setValidationVisibility] = useAsyncSafeState(false);
  const isFirstShipFromLocation = !useShipFroms().length;
  const { errors: validationErrors, isValid } = useYupFormValidation({
    form: contextShipFrom,
    schema: shipFromSchema,
  });

  const onSave = useCallback(async () => {
    setLoading(true);
    const { request, action } = getRequestAndAction(contextShipFrom.shipFromId);
    if (!isValid) {
      setValidationVisibility(true);
      setLoading(false);
      return;
    }
    try {
      const result = await request(contextShipFrom, shouldCreateInventoryWarehouse);

      if (contextShipFrom.ltlDefaults && result.shipFromId) {
        try {
          await saveShipFromLtlDefaults(String(result.shipFromId), contextShipFrom.ltlDefaults);
          result.ltlDefaults = contextShipFrom.ltlDefaults;
        } catch {
          // The main ship-from record saved correctly; freight defaults failed.
          // Show a warning (not a hard error) so the user knows to retry.
          NotificationService.showNotification({
            notificationType: NOTIFICATION_TYPE_WARNING,
            content: intl.formatMessage(shipFromContextMessages.ltlDefaultsPartialSaveWarning),
          });
        }
      }

      dispatch(action(result));
      trackShipFromConfigComplete(result, isFirstShipFromLocation, source);
      if (shouldCreateInventoryWarehouse) {
        getInventoryWarehouses().promise.then((response) => {
          const updateWarehouses = response?.data || [];
          dispatch(updateSellerInventoryWarehouses(updateWarehouses));
          onSuccess(result, updateWarehouses);
        });
      } else {
        onSuccess(result);
      }
    } catch (err) {
      setErrors(err.Errors || emptyArray);
      onError(err);
    }
    setLoading(false);
  }, [
    setLoading,
    contextShipFrom,
    isValid,
    setValidationVisibility,
    dispatch,
    isFirstShipFromLocation,
    source,
    onSuccess,
    setErrors,
    onError,
    shouldCreateInventoryWarehouse,
    intl,
  ]);

  const onSaveDraft = useCallback(() => {
    onShipFromBack(contextShipFrom);
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [contextShipFrom, onBack]);

  const updateShipFrom = useCallback(
    (updatedProperties) => {
      setContextShipFrom((prevState) => ({
        ...prevState,
        ...updatedProperties,
      }));
    },
    [setContextShipFrom],
  );

  useEffect(() => {
    if (!prefillAddress || Object.keys(prefillAddress).length === 0) return;

    setContextShipFrom((prev) => {
      const newOrigin = {
        countryCode: prev.originAddress.countryCode,
        ianaTimeZone: prev.originAddress.ianaTimeZone,
        ...prefillAddress,
      };
      const updatedShipFrom = { ...prev, originAddress: newOrigin };
      if (returnAddressSameAsShipFrom) {
        updatedShipFrom.returnAddress = newOrigin;
      }
      return updatedShipFrom;
    });

    dispatch(clearShippyPendingPrefill());
  }, [prefillAddress, returnAddressSameAsShipFrom, dispatch]);

  const value = useMemo(() => {
    return {
      shipFrom: contextShipFrom,
      updateShipFrom,
      validationErrors,
      isValid,
      returnAddressSameAsShipFrom,
      setReturnAddressSameAsShipFrom,
      onSave,
      onSaveDraft,
      showValidation,
      errors,
      setErrors,
      shouldHideTimezone,
      trackInputChange,
      trackInputClick,
      sources,
      isInventorySourcesLoading,
      loading,
    };
  }, [
    contextShipFrom,
    updateShipFrom,
    validationErrors,
    isValid,
    returnAddressSameAsShipFrom,
    onSave,
    onSaveDraft,
    showValidation,
    errors,
    setErrors,
    shouldHideTimezone,
    trackInputChange,
    trackInputClick,
    sources,
    isInventorySourcesLoading,
    loading,
  ]);

  return <ShipFromContext.Provider value={value}>{children}</ShipFromContext.Provider>;
};
